import { useState, useEffect } from "react";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { ref, onValue, off, update, get } from "firebase/database";
import { db } from "../../../configs/Firebase";
import PersonaLinkSelector from "../../personas/components/PersonaLinkSelector";
import style from "./LinkPersonasDialog.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";

const LinkPersonasDialog = ({ show, onClose, journeyId, journeyName, orgId }) => {
  const [linkedPersonaIds, setLinkedPersonaIds] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load linked personas for this journey
  useEffect(() => {
    if (orgId && journeyId) {
      const linkedPersonasRef = ref(db, `userData/${orgId}/journeys/${journeyId}/linkedPersonas`);
      
      const unsubscribe = onValue(linkedPersonasRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const personaIds = Object.keys(data);
          setLinkedPersonaIds(personaIds);
        } else {
          setLinkedPersonaIds([]);
        }
      });

      return () => {
        off(linkedPersonasRef);
      };
    }
  }, [orgId, journeyId]);

  // Load all personas to get names
  useEffect(() => {
    if (orgId) {
      const personasRef = ref(db, `userData/${orgId}/personas`);
      
      const unsubscribe = onValue(personasRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const personasList = Object.keys(data)
            .map(id => ({
              id,
              ...data[id]
            }))
            .filter(p => !p.archived);
          setPersonas(personasList);
        } else {
          setPersonas([]);
        }
      });

      return () => {
        off(personasRef);
      };
    }
  }, [orgId]);

  const handleSave = async () => {
    if (!orgId || !journeyId) return;

    setSaving(true);
    try {
      const { get } = await import("firebase/database");
      
      // Get current linked personas
      const currentLinksRef = ref(db, `userData/${orgId}/journeys/${journeyId}/linkedPersonas`);
      const currentSnapshot = await get(currentLinksRef);
      const currentData = currentSnapshot.val() || {};
      const currentPersonaIds = Object.keys(currentData);

      // Add new links
      const updates = {};
      const personaUpdates = {};
      
      for (const personaId of linkedPersonaIds) {
        const persona = personas.find(p => p.id === personaId);
        if (persona) {
          // Link journey to persona
          updates[`userData/${orgId}/journeys/${journeyId}/linkedPersonas/${personaId}`] = {
            personaId,
            personaName: persona.name,
            linkedAt: { ".sv": "timestamp" }
          };

          // Update persona's linkedJourneys
          const existingJourneys = persona.linkedJourneys || [];
          const journeyLink = {
            journeyId,
            journeyName: journeyName || "Untitled Journey"
          };
          
          // Check if journey is already linked
          if (!existingJourneys.find(j => j.journeyId === journeyId)) {
            const updatedJourneys = [...existingJourneys, journeyLink];
            personaUpdates[`userData/${orgId}/personas/${personaId}/linkedJourneys`] = updatedJourneys;
          }
        }
      }

      // Remove links that are no longer selected
      for (const currentPersonaId of currentPersonaIds) {
        if (!linkedPersonaIds.includes(currentPersonaId)) {
          // Remove from journey
          updates[`userData/${orgId}/journeys/${journeyId}/linkedPersonas/${currentPersonaId}`] = null;
          
          // Remove from persona
          const persona = personas.find(p => p.id === currentPersonaId);
          if (persona && persona.linkedJourneys) {
            const updatedJourneys = persona.linkedJourneys.filter(j => j.journeyId !== journeyId);
            personaUpdates[`userData/${orgId}/personas/${currentPersonaId}/linkedJourneys`] = updatedJourneys;
          }
        }
      }

      // Apply all updates
      if (Object.keys(updates).length > 0 || Object.keys(personaUpdates).length > 0) {
        const allUpdates = { ...updates, ...personaUpdates };
        await update(ref(db), allUpdates);
      }

      onClose();
    } catch (error) {
      console.error("Error linking personas:", error);
      alert("Failed to link personas");
      setSaving(false);
    }
  };

  return (
    <Dialog
      show={show}
      onClose={onClose}
      title="Link Personas to Journey"
      variants={["large"]}
    >
      <div className={style.dialog}>
        <p className={style.description}>
          Link personas to this journey to show how different user types experience it.
        </p>
        
        <PersonaLinkSelector
          selectedPersonaIds={linkedPersonaIds}
          onChange={setLinkedPersonaIds}
          multiselect={true}
          orgId={orgId}
        />

        <div className={style.linkedList}>
          {linkedPersonaIds.length > 0 && (
            <>
              <h4>Linked Personas:</h4>
              <ul>
                {linkedPersonaIds.map(personaId => {
                  const persona = personas.find(p => p.id === personaId);
                  return persona ? (
                    <li key={personaId}>
                      <Icon>person</Icon> {persona.name}
                    </li>
                  ) : null;
                })}
              </ul>
            </>
          )}
        </div>

        <div className={style.actions}>
          <Button variants={["text"]} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Links"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default LinkPersonasDialog;

